import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/rootReducer";
import { useEffect, useState } from "react";
import {
  Button,
  Pagination,
  Table,
  TableProps,
  Input,
  Spinner,
  Header,
  Alert,
} from "@amzn/awsui-components-react";
import {
  CollectionPreferences,
  FormField,
  Icon,
  Modal,
} from "@amzn/awsui-components-react/polaris";
import { getMarketplaceId } from "../../from-server/acs/acs-service";
import { decryptCustomerId } from "@amzn/amazon-id";
import {
  setAsinMarketplaceWeblab,
  setAsinMarketplaceWeblabReason,
  setStamperWizardState,
} from "./stamperWizardConnector";
import { getLiveness } from "../app/api";
import { trackComponentView } from "../telemetry/telemetry";

export interface AsinMarketplaceWeblab {
  asin: string;
  marketplaceId: string;
  liveness: string;
  waitingForLiveness: boolean;
  reasonForWeblab: string;
}

export const WeblabTable = () => {
  const dispatch = useDispatch();
  const {
    asinsMarketplaceLiveness,
    touched,
    asinsWithListOfMarketplaces,
    asinMarketplaceWeblab,
    weblabReasonHashLookup,
  } = useSelector((state: RootState) => state.stamperWizard);
  const { editableAttributes, isGettingEditableAttributes } = useSelector(
    (state: RootState) => state.porAsinList
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [weblabHasIssues, setWeblabHasIssues] = useState<boolean>(false);
  const [asinMarketplaceWeblabs, setAsinMarketplaceWeblabs] = useState<
    AsinMarketplaceWeblab[]
  >([]);
  const weblabCheck = (asinMpWeblab: AsinMarketplaceWeblab) =>
    asinMpWeblab.liveness == "error" ||
    asinMpWeblab.liveness == "" ||
    (asinMpWeblab.liveness != "not_live" && !asinMpWeblab.reasonForWeblab);

  useEffect(() => {
    trackComponentView("stamper", "weblabTable");
  }, []);

  useEffect(() => {
    asinMarketplaceWeblabs.forEach((item) => {
      dispatch(
        setAsinMarketplaceWeblabReason({
          asinMp: `${item.asin}#${item.marketplaceId}`,
          weblabReason: item.reasonForWeblab,
        })
      );
    });
    setWeblabHasIssues(asinMarketplaceWeblab.some(weblabCheck));
  }, [asinMarketplaceWeblabs]);

  useEffect(() => {
    const temp = asinsWithListOfMarketplaces
      .map((asinMp) => {
        const asin = asinMp.asin;
        return asinMp.marketplaces.map((mp: any) => {
          if (`${asinMp.asin}#${mp}#weblab` in asinsMarketplaceLiveness) {
            return {
              asin: asin,
              marketplaceId: mp,
              liveness:
                asinsMarketplaceLiveness[`${asinMp.asin}#${mp}#weblab`]
                  .liveness,
              waitingForLiveness:
                asinsMarketplaceLiveness[`${asinMp.asin}#${mp}#weblab`]
                  .waitingForLiveness,
              reasonForWeblab:
                weblabReasonHashLookup[`${asinMp.asin}#${mp}`] || "",
            } as AsinMarketplaceWeblab;
          } else {
            dispatch(
              setStamperWizardState({
                item: `${asinMp.asin}#${mp}#weblab`,
                value: { liveness: "", waitingForLiveness: false },
              })
            );
            return {
              asin: asin,
              marketplaceId: mp,
              liveness: "",
              waitingForLiveness: false,
              reasonForWeblab:
                weblabReasonHashLookup[`${asinMp.asin}#${mp}`] || "",
            } as AsinMarketplaceWeblab;
          }
        });
      })
      .flat();
    setAsinMarketplaceWeblabs(temp);
    dispatch(setAsinMarketplaceWeblab({ asinMarketplaceWeblab: temp }));
  }, [asinsWithListOfMarketplaces, asinsMarketplaceLiveness]);

  function validationWeblabReason(weblabReason: string) {
    if (!weblabReason) {
      return undefined;
    } else if (weblabReason.length > 64) {
      return "You have exceeded the maximum length allowed";
    } else if (weblabReason.trim().length == 0) {
      return "You cannot submit an empty string as weblab reason";
    }
    return undefined;
  }

  function retrieveLiveness(item: AsinMarketplaceWeblab) {
    const marketNumber = Number(
      decryptCustomerId(getMarketplaceId(item.marketplaceId) || "")
    );
    dispatch(
      setStamperWizardState({
        item: `${item.asin}#${item.marketplaceId}#weblab`,
        value: { liveness: "", waitingForLiveness: true },
      })
    );
    getLiveness(item.asin, marketNumber)
      .then((result) => {
        dispatch(
          setStamperWizardState({
            item: `${item.asin}#${item.marketplaceId}#weblab`,
            value: { liveness: result.liveStatus, waitingForLiveness: false },
          })
        );
      })
      .catch(() => {
        dispatch(
          setStamperWizardState({
            item: `${item.asin}#${item.marketplaceId}#weblab`,
            value: { liveness: "error", waitingForLiveness: false },
          })
        );
      });
  }

  const handleEditSubmitNoClick = async (
    currentItem: AsinMarketplaceWeblab,
    newValue: unknown
  ) => {
    const newItem = { ...currentItem, reasonForWeblab: newValue as string };
    const temp = asinMarketplaceWeblabs.map((item) => {
      if (
        item.asin == newItem.asin &&
        item.marketplaceId == newItem.marketplaceId
      ) {
        return newItem;
      } else {
        return item;
      }
    });
    setAsinMarketplaceWeblabs(temp);
    dispatch(setAsinMarketplaceWeblab({ asinMarketplaceWeblab: temp }));
  };

  const columnDefinitions: TableProps.ColumnDefinition<AsinMarketplaceWeblab>[] =
    [
      {
        id: "asin",
        header: "ASIN",
        width: "15%",
        cell: (item) => item.asin,
      },
      {
        id: "marketplaces",
        header: "Marketplace",
        width: "15%",
        cell: (item) => {
          return <span> {item.marketplaceId} </span>;
        },
      },
      {
        id: "liveness",
        header: "Liveness",
        width: "20%",
        cell: (item) => {
          if (item.liveness === "" && !item.waitingForLiveness) {
            retrieveLiveness(item);
          }
          return item.waitingForLiveness ? (
            <Spinner></Spinner>
          ) : item.liveness == "error" ? (
            <span>
              <Icon size="small" variant="error" name="status-negative" />{" "}
              <Button
                onClick={() => {
                  retrieveLiveness(item);
                }}
              >
                Retry
              </Button>
            </span>
          ) : (
            <span>{item.liveness}</span>
          );
        },
      },
      {
        id: "reason",
        header: "Reason For Weblabing potentially Live ASIN",
        width: "40%",
        editConfig: {
          ariaLabel: "Name",
          editIconAriaLabel: "editable",
          errorIconAriaLabel: "Name Error",
          validation(item, value) {
            return validationWeblabReason(value);
          },
          editingCell: (item, { currentValue, setValue }) => {
            return (
              <Input
                invalid={item.liveness != "not_live" && !item.reasonForWeblab}
                value={item.reasonForWeblab}
                onChange={(event) => {
                  setValue(event.detail.value);
                  if (!validationWeblabReason(event.detail.value)) {
                    handleEditSubmitNoClick(item, event.detail.value);
                  }
                }}
              />
            );
          },
          disabledReason: (item) => {
            if (item.liveness == "not_live") {
              return "No reason needed";
            } else if (item.liveness == "error") {
              return "Liveness required to edit reason";
            }
            return undefined;
          },
        },
        cell: (item) => {
          return item.liveness == "error" && !item.waitingForLiveness ? (
            <span> </span>
          ) : item.liveness != "not_live" &&
            item.reasonForWeblab == "" &&
            !item.waitingForLiveness ? (
            <span>
              {" "}
              <Icon size="small" variant="error" name="status-negative" />{" "}
              <i>A Reason is required</i>{" "}
            </span>
          ) : (
            <span> {item.reasonForWeblab} </span>
          );
        },
      },
    ];
  return (
    <>
      <FormField
        errorText={
          touched["3"] && weblabHasIssues
            ? "A liveness value or weblab reason is missing. Make sure to check all pages"
            : undefined
        }
        stretch={true}
      >
        <Alert statusIconAriaLabel="Info">
          Verify each ASIN/Marketplace has a Liveness value and press Retry if
          the API call return an Error. You must have a Liveness value for every
          ASIN to submit a Weblab request. If the Liveness is anything other
          than "not_live", you must a provide a reason for weblabing.
        </Alert>
        <Table
          header={<Header variant={"h3"}>Added ASINs (Weblab)</Header>}
          variant={"borderless"}
          enableKeyboardNavigation={true}
          resizableColumns={true}
          columnDefinitions={columnDefinitions}
          items={asinMarketplaceWeblabs.slice(
            pageNumber * pageSize - pageSize,
            pageNumber * pageSize
          )}
          pagination={
            <Pagination
              currentPageIndex={pageNumber}
              pagesCount={Math.ceil(asinMarketplaceWeblabs.length / pageSize)}
              onChange={({ detail }) => setPageNumber(detail.currentPageIndex)}
            />
          }
          preferences={
            <CollectionPreferences
              onConfirm={(event) => {
                setPageSize(event.detail.pageSize || 10);
              }}
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={{
                pageSize: pageSize,
              }}
              pageSizePreference={{
                title: "Page size",
                options: [
                  { value: 10, label: "10 records" },
                  { value: 20, label: "20 records" },
                  { value: 50, label: "50 records" },
                ],
              }}
            />
          }
        />
      </FormField>
    </>
  );
};
